import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import Cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";
import SchemaInfo from "../common/SchemaInfo";
import nodeService from "../../services/nodeService";
import SelectPane from "./SelectPane";
import DraftDocumentModal from "../common/DraftDocumentModal";
import FileNameModal from "../common/FileNameModal";
import { useSelector, useDispatch } from "react-redux";
import {
  clearFilterAndCopyLink,
  collapseExpandedNodes,
  fetchFilteredGraphData,
  fetchPinnedNodes,
  setFetchedNodesData,
  setInitialNodesCount,
  toggleCollapseAll,
  updateEdgeCount,
} from "../../redux/slices/graphSlice";
import api from "../../api/apiClient";
import NodeContextMenu from "../common/NodeContextMenu";
import LinkCopyModal from "../common/LinkCopyModal";
import { useSchemaGraph } from "../../hooks/useSchemaGraph";
import { IoResize } from "react-icons/io5";
import { FiPlus, FiMinus } from "react-icons/fi";
import EdgeInfoTab from "../common/EdgeInfoTab";
import { toast } from "react-toastify";
import { useUserId } from "../../Utils/helpers";
import { ViewContext } from "../../context/ViewContext";
import { useSidebar } from "../../context/SidebarContext";
import { baseURL } from "../../env/env";
import { Spinner } from "react-bootstrap";
import { Hoverbar } from "../common/HovrBar";

Cytoscape.use(dagre);
// Cytoscape.use(cxtmenu);

const NodeSchemaViewPane = ({ isPlaybookLink }) => {
  const containerRef = useRef(null);
  const { cy: cyschema } = useSchemaGraph();
  const { REACT_APP_API_ENDPOINT } = process.env;
  const dispatch = useDispatch();
  const {
    pinnedNodes,
    graphData,
    expandedNodes,
    isCollapseAll,
    allExpandedNodes,
    filterNode,
    isCopyLink,
    expandedNodesMap,
    fetchedNodesData,
    expandedPinnedNodes,
  } = useSelector((state) => state.graph);
  const selectedLeafClasses = useSelector(
    (state) => state.graph.selectedLeafClasses
  );

  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.userRole === "superuser";

  const [loadingNodes, setLoadingNodes] = useState(false);
  const { resetRequested, completeReset, attachments, setAttachments } =
    useSidebar();
  const { showDraftModals, setShowDraftModals } = useSidebar();

  const currentRole = useSelector((state) => state.auth.currentRole);
  const { nodes = [], edges = [] } = graphData ?? {};

  const [fetchedData, setFetchedData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlaybook, setIsPlaybook] = useState(false);
  const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [showEdgeCounts, setShowEdgeCounts] = useState(false);
  const [hasFetchedChildNodes, setHasFetchedChildNodes] = useState(false);
  const [showPane, setShowPane] = useState(false);
  const [selectedNodeOid, setSelectedNodeOid] = useState(null);
  const [clickedNode, setClickedNode] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [isInfoModal, setIsInfoModal] = useState(false);
  const [filename, setFilename] = useState("");
  const [showFileModal, setShowFileModal] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkNodeName, setLinkNodeName] = useState("");
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]); // Manage selected nodes state here
  const [selectedEdge, setSelectedEdge] = useState(null);
  const { viewType } = useContext(ViewContext);
  const [message, setMessage] = useState("");
  const [newEdge, setNewEdge] = useState(null);
  const [newNode, setNewNode] = useState(null);
  const [uniqueNodes, setUniqueNodes] = useState(nodes);
  const [uniqueEdges, setUniqueEdges] = useState(edges);
  const [isClose, setIsClose] = useState(false);

  const cyRef = useRef(null);
  const userId = useUserId();
  const zoomInInterval = useRef(null);
  const zoomOutInterval = useRef(null);
  const draftedNodes = useRef([]);

  const defaultLayout = {
    randomize: false,
    name: "cola",
    animate: true,
    refresh: 2,
    maxSimulationTime: 100,
    fit: true,
    handleDisconnected: true,
    infinite: false,
    ungrabifyWhileSimulating: false,
    nodeDimensionsIncludeLabels: true,
    convergenceThreshold: 0.1,
    nodeSpacing: function (node) {
      return 30;
    },
    ready: () => {},
    stop: () => {},
    unconstrIter: 10,
    userConstIter: 10,
    allConstIter: 0,
    gridifyIter: 5,
    edgeLength: (edge) => {
      const nodes = edge.connectedNodes();
      const degs = nodes.minDegree();

      // Base length based on node degree
      let baseLength = 100 + 65 * Math.sqrt(degs - 1);

      // Get the label and measure its length
      const edgeData = edge.data();
      const label = edgeData.label || "";
      const labelLength = label.length;

      // Calculate edge length dynamically based on label length
      const lengthAdjustment = labelLength * 5; // Adjust multiplier as needed for spacing
      return baseLength + lengthAdjustment;
    },
  };

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      const handleContextMenu = (event) => {
        event.preventDefault(); // Prevent the default right-click context menu
      };

      container.addEventListener("contextmenu", handleContextMenu);

      return () => {
        container.removeEventListener("contextmenu", handleContextMenu); // Cleanup on component unmount
      };
    }
  }, []);

  useEffect(() => {
    const combinedNodes = [
      ...new Map([
        ...nodes.map((node) => [JSON.stringify(node), node]),
        ...(newNode ?? []).map((node) => [JSON.stringify(node), node]),
        ...(fetchedNodesData.nodes ?? []).map((node) => [
          JSON.stringify(node),
          node,
        ]),
      ]).values(),
    ];

    // Only update if the combined nodes are different from the current uniqueNodes
    if (JSON.stringify(combinedNodes) !== JSON.stringify(uniqueNodes)) {
      setUniqueNodes(combinedNodes);
    }
  }, [nodes, newNode, fetchedNodesData.nodes, uniqueNodes]);

  useEffect(() => {
    const combinedEdges = [
      ...new Map([
        ...edges.map((edge) => [JSON.stringify(edge), edge]),
        ...(newEdge ?? []).map((edge) => [JSON.stringify(edge), edge]),
        ...(fetchedNodesData.edges ?? []).map((edge) => [
          JSON.stringify(edge),
          edge,
        ]),
      ]).values(),
    ];

    // Only update if the combined edges are different from the current uniqueEdges
    if (JSON.stringify(combinedEdges) !== JSON.stringify(uniqueEdges)) {
      setUniqueEdges(combinedEdges);
    }
  }, [edges, newEdge, fetchedNodesData.edges, uniqueEdges]);

  useEffect(() => {
    if (showDraftModals) {
      setShowDraftModal(true);

      setTimeout(() => {
        setShowDraftModals(false);
      }, 1000);
    }
  }, [showDraftModals]);

  useEffect(() => {
    draftedNodes.current = attachments;
  }, [attachments]);

  useEffect(() => {
    return () => {
      stopContinuousZoom(); // Clean up intervals on component unmount
    };
  }, []);

  useEffect(() => {
    setIsModalOpen(false);
    setIsEdgeModalOpen(false);
  }, [viewType]);

  useEffect(() => {
    if (showDraftModal) {
      closeEdgeModal();
      closeNodeModal();
    }
  }, [showDraftModal]);

  useEffect(() => {
    if (isCollapseAll && expandedNodes.size > 0) {
      if (cyRef.current) {
        const nodesToCollapse = cyRef.current.collection(
          Array.from(allExpandedNodes)
            .map((elem) => cyRef.current.getElementById(String(elem)))
            .filter((node) => node && !pinnedNodes.has(node.id()))
        );
        const edgesToRemove = nodesToCollapse.connectedEdges();

        cyRef.current.batch(() => {
          edgesToRemove.remove();
          nodesToCollapse.remove();
        });

        cyRef.current.nodes().forEach((node) => node.data("expanded", false));

        dispatch(collapseExpandedNodes());
        dispatch(toggleCollapseAll());
        // dispatch(fetchPinnedNodes({ userId }));
      }
    }
  }, [
    isCollapseAll,
    cyRef.current,
    expandedNodes,
    dispatch,
    pinnedNodes,
    allExpandedNodes,
  ]);

  const handleDraftModalClose = () => {
    setShowDraftModal(false);
    setIsInfoModal(false);
  };

  const generateNodeLink = (nodeId, data = null) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/knowledge/#filter_includeManual=${nodeId}&select=${nodeId}&v=1&title=${encodeURIComponent(
      data?.props?.name
    )}`;
  };

  const handleFileModalClose = () => {
    setShowFileModal(false);
    setFilename("");
  };

  const downloadFile = (fileUrl) => {
    fetch(fileUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = filename.trim();
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.error("Draft could not be downloaded");
      });
  };

  const handleDownload = async () => {
    const sanitizedFilename = filename.trim();

    if (!sanitizedFilename) {
      toast.error("Please enter a valid filename.");
      return;
    }

    if (attachments.length === 0) {
      toast.error("Please add attachments before downloading.");
      return;
    }

    const payload = {
      docArray: attachments.map((att) => att.attachment),
      filename: `${sanitizedFilename}.docx`,
    };

    try {
      const response = await api.post("/draft_documents", payload);

      if (response.status === 200) {
        const fileUrl = `${baseURL}/draft_documents/${sanitizedFilename}.docx`;
        downloadFile(fileUrl);
      } else {
        console.error("Failed to create the file:", response.data);
        toast.error("Failed to create the document.");
      }
    } catch (error) {
      console.error("Error occurred while creating the document:", error);
      toast.error("Failed to create the document.");
    }

    setAttachments([]);
    handleFileModalClose();
  };

  const handleClearAttachments = () => {
    setAttachments([]);
  };

  const handleDraftAction = () => {
    setShowFileModal(true);
    handleDraftModalClose();
    closeNodeModal();
    closeEdgeModal();
  };

  const toggleLinkModal = () => {
    setIsLinkModalOpen(!isLinkModalOpen);
    closeNodeModal();
    closeEdgeModal();
  };

  const closeNodeModal = () => {
    setIsClose(true);
    setIsModalOpen(false);
  };

  const closeEdgeModal = () => {
    setIsEdgeModalOpen(false);
  };

  const handleSelectClick = (oid, nodeData) => {
    setSelectedNodeOid(oid);
    setClickedNode(nodeData);
    setShowPane(true);
    closeNodeModal();
    closeEdgeModal();
  };

  const handleDeselectClick = (oid) => {
    setSelectedNodeOid(null);
    setClickedNode(null);
    setSelectedNodes((prevSelectedNodes) =>
      prevSelectedNodes.filter((node) => {
        return node.node.props._oid !== oid;
      })
    );
  };

  const fetchFurtherPlaybookRules = (ruleId, visited = new Set()) => {
    if (visited.has(ruleId)) {
      return [];
    }

    visited.add(ruleId);

    const furtherRules = uniqueEdges
      .filter((edge) => edge.class === "IsAnswerFor" && edge.source === ruleId)
      .map((edge) => {
        const relatedNodeId =
          edge.source === ruleId ? edge.target : edge.source;

        return uniqueNodes.find(
          (node) => node.id === relatedNodeId && node.class === "PlaybookRule"
        );
      });
    return furtherRules;
  };

  const updateNodeLabels = useCallback(() => {
    if (cyRef.current) {
      cyRef.current.nodes().forEach((node) => {
        // Skip updating the label if the node is expanded
        // if (node.data("expanded")) {
        //   return;
        // }

        const edgeCount = node.connectedEdges().length;
        const currentLabel = node.data("label")?.split("[")[0].trim();
        const newLabel = showEdgeCounts
          ? `${currentLabel} [${edgeCount}]`
          : currentLabel;
        node.data("label", newLabel);
      });
    }
  }, [showEdgeCounts]);

  const handleDeleteEdge = async (relationId) => {
    try {
      const response = await fetch(
        `${REACT_APP_API_ENDPOINT}/relations/${relationId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": "6XxnCdOnCfwHEKwM",
          },
        }
      );
      try {
        if (response.status === 204) {
          const edge = [
            ...edges,
            ...(newEdge ?? []),
            ...(fetchedNodesData.edges ?? []),
          ].find((edge) => edge.id === relationId);
          const sourceNode = [
            ...nodes,
            ...(newEdge ?? []),
            ...(fetchedNodesData.nodes ?? []),
          ].find((node) => node.id === edge.source);
          await fetchTopicNodesCount(sourceNode.id);

          toast.success("Edge deleted successfully");
          dispatch(fetchFilteredGraphData({ userId: userId }));
          if (cyRef.current?.nodes()) {
            const cyNodeIds = new Set(
              cyRef.current.nodes().map((node) => parseInt(node.id(), 10))
            );

            // Check if all cyNodeIds are in pinnedNodes
            const allNodesPresent = [...cyNodeIds].every((id) =>
              pinnedNodes.has(String(id))
            );

            if (allNodesPresent) {
              dispatch(fetchPinnedNodes({ userId }));
            }
          }
        }
      } catch (error) {
        console.error("Error while deleting edge:", error);
        toast.error("An error occurred while deleting the edge.");
      }

      closeEdgeModal();
    } catch (error) {
      toast.error("Failed to delete edge");
    }
  };

  // First add this ref at component level
  const expandedNodesDataRef = useRef({
    nodes: new Map(),
    edges: new Map(),
  });

  const getAllNodes = useCallback(
    async (nodeId) => {
      setLoadingNodes(true);
      const clickedNode = uniqueNodes.find(
        (node) => node.id === parseInt(nodeId)
      );
      const nodeOId = clickedNode ? clickedNode.props._oid : null;

      if (!nodeOId) {
        console.error("Node ID (_oid) is not found in the element data.");
        setLoadingNodes(false);
        return;
      }

      try {
        const childNodesResponse = await nodeService.fetchChildNodes(
          nodeOId,
          userId
        );

        // Store the fetched data for expanded nodes
        if (childNodesResponse.nodes) {
          childNodesResponse.nodes.forEach((node) => {
            expandedNodesDataRef.current.nodes.set(String(node.id), node);
          });
        }
        if (childNodesResponse.edges) {
          // Filter edges to remove self-loops (edges where source and target are the same)
          const filteredEdges = childNodesResponse.edges.filter(
            (edge) => edge.source !== edge.target
          );
          childNodesResponse.edges = filteredEdges;
          childNodesResponse.edges.forEach((edge) => {
            expandedNodesDataRef.current.edges.set(String(edge.id), edge);
          });
        }

        // Store fetched data in Redux
        dispatch(
          setFetchedNodesData({
            nodes: childNodesResponse.nodes || [],
            edges: childNodesResponse.edges || [],
          })
        );

        setFetchedData({
          nodes: childNodesResponse.nodes,
          edges: childNodesResponse.edges,
        });

        setHasFetchedChildNodes(true);
        setShowEdgeCounts(true);
        updatePinnedNodeStyles();

        dispatch(
          updateEdgeCount(cyRef.current?.nodes()?.length - pinnedNodes.size)
        );
      } catch (error) {
        console.error("Failed to fetch child nodes:", error);
      } finally {
        setLoadingNodes(false);
      }
    },
    [nodes, newNode, fetchedNodesData, uniqueNodes, dispatch]
  );

  const fetchTopicNodesCount = async (nodeId) => {
    setLoadingNodes(true);
    const clickedNode = uniqueNodes.find(
      (node) => node.id === parseInt(nodeId)
    );
    const nodeOId = clickedNode ? clickedNode.props._oid : null;

    if (!nodeOId) {
      console.error("Node ID (_oid) is not found in the element data.");
      setLoadingNodes(false);
      return;
    }

    try {
      const childNodesResponse = await nodeService.fetchChildNodes(
        nodeOId,
        userId
      );

      // Store the fetched data for expanded nodes
      if (childNodesResponse.nodes) {
        childNodesResponse.nodes.forEach((node) => {
          expandedNodesDataRef.current.nodes.set(String(node.id), node);
        });
      }
      if (childNodesResponse.edges) {
        const filteredEdges = childNodesResponse.edges.filter(
          (edge) => edge.source !== edge.target
        );
        childNodesResponse.edges = filteredEdges;
        childNodesResponse.edges.forEach((edge) => {
          expandedNodesDataRef.current.edges.set(String(edge.id), edge);
        });
      }

      // Store fetched data in Redux
      dispatch(
        setFetchedNodesData({
          nodes: childNodesResponse.nodes || [],
          edges: childNodesResponse.edges || [],
        })
      );

      setFetchedData({
        nodes: childNodesResponse.nodes,
        edges: childNodesResponse?.edges,
      });

      setHasFetchedChildNodes(true);
      setShowEdgeCounts(true);
      updatePinnedNodeStyles();
    } catch (error) {
      console.error("error while fetching nodes", error);
    } finally {
      setLoadingNodes(false);
    }
  };

  const handleClearFilterAndCopy = () => {
    dispatch(clearFilterAndCopyLink());
  };

  // Add cleanup for expandedNodesDataRef
  useEffect(() => {
    return () => {
      expandedNodesDataRef.current = {
        nodes: new Map(),
        edges: new Map(),
      };
    };
  }, []);

  const preFilterGraphData = (selectedNodes, validEdges, topics) => {
    let finalNodes = new Set();
    let finalEdges = new Set();

    // Process each topic separately
    topics.forEach((topic) => {
      // Find Topic node for current topic
      const topicNode = selectedNodes.find(
        (node) => node.class === "Topic" && node.props.name === topic
      );

      if (!topicNode) return; // Skip if topic not found

      // Find HasTopic edges for this topic
      const hasTopicEdges = validEdges.filter(
        (edge) =>
          edge.class === "HasTopic" &&
          (edge.source === topicNode.id || edge.target === topicNode.id)
      );

      // Find Playbook nodes connected to this topic
      const playbookNodes = selectedNodes.filter((node) => {
        if (node.id === topicNode?.id) return false;
        return hasTopicEdges.some(
          (edge) => edge.source === node.id || edge.target === node.id
        );
      });

      if (playbookNodes.length > 0) {
        // If there are playbook nodes, add them and their edges
        playbookNodes.forEach((node) => finalNodes.add(node));
        hasTopicEdges.forEach((edge) => finalEdges.add(edge));
      }
      // else {
      //   // If no playbook nodes, add the topic node
      //   finalNodes.add(topicNode);
      // }
    });

    return {
      filteredNodes: Array.from(finalNodes),
      filteredEdges: Array.from(finalEdges),
    };
  };

  const instanceNodeRemote2Local = (node) => ({
    group: "nodes",
    classes: node?.labels?.map((x) => `node-${x}`).join(" "),
    data: {
      id: node?.id?.toString(),
      label: node?.labels,
      display: node?.display,
      cls: node?.class,
      color: node?.colour,
      name: node?.props?.name,
      prop_name: node?.props?.name,
      props: node?.props,
      oid: node?.props?._oid,
      is_instance: true,
    },
  });

  const instanceEdgeRemote2Local = (edge) => ({
    group: "edges",
    data: {
      id: edge.id.toString(),
      cls: edge.class,
      display:
        edge.display +
        (edge.props.value ? "=" + edge.props.value.slice(0, 20) : ""),
      label:
        edge.class === "IsAnswerFor" && edge.props.value
          ? `${edge.display} = ${edge.props.value}`
          : edge.display || "",
      source: edge.source.toString(),
      target: edge.target.toString(),
      props: edge.props,
      is_instance: true,
    },
  });

  const updatePinnedNodeStyles = () => {
    if (cyRef.current) {
      cyRef.current.nodes().forEach((node) => {
        if (pinnedNodes.has(node.id())) {
          // If the node is pinned, apply the red border style
          node.style({
            "border-width": 5,
            "border-color": "red",
            "border-style": "double",
            "text-outline-width": 2,
          });
        } else if (expandedNodes.has(parseInt(node.id()))) {
          // If the node is expanded, apply the blue border style
          node.style({
            "border-width": 5,
            "border-color": "blue",
            "border-style": "double",
            "text-outline-width": 2,
          });
        } else {
          // If the node is neither pinned nor expanded, remove the border
          node.style({
            "border-width": 0,
          });
        }
      });
    }
  };

  const preProcessEdges = (allValidEdges) => {
    const edgeMap = { incoming: {}, outgoing: {} };

    allValidEdges.forEach((edge) => {
      if (!cyRef.current.getElementById(edge.id).length) {
        // Only process hidden edges
        if (!edgeMap.incoming[edge.target]) edgeMap.incoming[edge.target] = 0;
        if (!edgeMap.outgoing[edge.source]) edgeMap.outgoing[edge.source] = 0;

        edgeMap.incoming[edge.target]++;
        edgeMap.outgoing[edge.source]++;
      }
    });

    return edgeMap;
  };

  const updatePaneNodeLabels = (allNodes, allValidEdges) => {
    const edgeMap = preProcessEdges(allValidEdges);
    const labelUpdates = [];

    allNodes.forEach((node) => {
      const incoming = edgeMap.incoming[node?.id] || 0;
      const outgoing = edgeMap.outgoing[node?.id] || 0;
      const totalHiddenEdges = incoming + outgoing;
      const newLabel =
        showEdgeCounts && totalHiddenEdges > 0
          ? `${node?.display}  [${totalHiddenEdges}]`
          : node?.display;
      const cyNode = cyRef.current.getElementById(node?.id);
      if (cyNode.data("label") !== newLabel) {
        labelUpdates.push({ node: cyNode, label: newLabel });
      }
    });

    // Apply label updates in one batch
    cyRef.current.batch(() => {
      labelUpdates.forEach(({ node, label }) => node.data("label", label));
    });
  };

  useEffect(() => {
    if (isModalOpen) {
      updatePinnedNodeStyles();
    }
  }, [isModalOpen, pinnedNodes]);

  useEffect(() => {
    if (graphData && containerRef.current) {
      let topicsSet = new Set(selectedLeafClasses);
      let topicNodes = nodes;
      // .filter(
      //   (n) => n.class === "Topic" && topicsSet.has(n.props.name)
      // );
      let selectedNodes = topicsSet?.size > 0 ? topicNodes : nodes ?? [];

      const fetchedNodesMap = new Map(
        [...(newNode ?? []), ...(fetchedNodesData.nodes ?? [])].map((node) => [
          node.id,
          node,
        ])
      );

      let expandedNodesDetails;
      const combinedExpandedNodes = new Set([
        ...allExpandedNodes,
        ...expandedNodes,
        ...(newNode?.map((node) => node.id) || []), // Provide a fallback empty array
      ]);
      expandedNodesDetails = Array.from(combinedExpandedNodes)
        .map((nodeId) => fetchedNodesMap.get(parseInt(nodeId)))
        .filter(Boolean);
      expandedNodesDetails = [
        ...new Map(
          expandedNodesDetails.map((node) => [node.id, node])
        ).values(),
      ];

      let pinnedNodesDetails = Array.from(pinnedNodes)
        .map(
          (nodeId) =>
            fetchedNodesMap.get(parseInt(nodeId)) ||
            expandedNodesDataRef.current.nodes.get(nodeId)
        )
        .filter(Boolean);

      pinnedNodesDetails = [
        ...new Map(pinnedNodesDetails.map((node) => [node.id, node])).values(),
      ];

      if (allExpandedNodes.size > 0) {
        if (expandedNodesDetails.length > 0) {
          selectedNodes = [...selectedNodes, ...expandedNodesDetails];
        }
      }
      if (newNode?.length > 0) {
        if (expandedNodesDetails.length > 0) {
          selectedNodes = [...selectedNodes, ...expandedNodesDetails];
        }
      }

      if (topicsSet.size > 0) {
        if (topicsSet.size > 0) {
          const { filteredNodes } = preFilterGraphData(
            selectedNodes,
            edges,
            selectedLeafClasses
          );
          // setIsPlaybook(false);
          selectedNodes = filteredNodes;
          if (expandedNodesDetails && expandedNodesDetails.length > 0) {
            selectedNodes = [...selectedNodes, ...expandedNodesDetails];
          }
          if (pinnedNodesDetails && pinnedNodesDetails.length > 0) {
            selectedNodes = [...selectedNodes, ...pinnedNodesDetails];
          }

          if (!selectedNodes || selectedNodes.length === 0) {
            setIsModalOpen(false);
            // toast.error("No records found ");
          } else {
            if (
              selectedNodes.length === 1 &&
              selectedNodes[0].class === "Playbook" &&
              !isClose
            ) {
              handleNodeTap(selectedNodes[0].id);
              setIsPlaybook(true);
              handleClearFilterAndCopy();
            }
          }
        } else {
          selectedNodes = nodes.filter(
            (n) => n.class === "Topic" && topicsSet.has(n.props.name)
          );
        }
      }

      if (isCopyLink) {
        // Filter based on your condition and then use a Set to ensure unique nodes by their id
        const seenIds = new Set();

        selectedNodes = selectedNodes.filter((n) => {
          const shouldInclude =
            (n?.props._oid === filterNode ||
              allExpandedNodes.has(String(n?.id))) &&
            !seenIds.has(n?.id);

          if (shouldInclude) {
            seenIds.add(n?.id); // Track this id to avoid duplicates
          }

          return shouldInclude;
        });

        if (!isCopyLink) {
          setIsPlaybook(false);
        }
        if (
          selectedNodes.length === 1 &&
          selectedNodes[0].class === "Playbook"
        ) {
          handleNodeTap(selectedNodes[0].id);
          setIsPlaybook(true);
        }
        // handleClearFilterAndCopy();
      }
      if (expandedPinnedNodes.size > 0) {
        const pinnedNodes = Array.from(expandedPinnedNodes)
          .map((nodeId) => fetchedNodesMap.get(parseInt(nodeId)))
          .filter(Boolean);

        selectedNodes = [
          ...new Map(
            [...selectedNodes, ...pinnedNodes].map((node) => [node.id, node])
          ).values(),
        ];
      }

      const nodeIds = new Set(selectedNodes?.map((node) => node?.id));
      const validEdges =
        (uniqueEdges.length
          ? uniqueEdges
          : fetchedNodesData.edges ?? []
        ).filter(
          (edge) => nodeIds.has(edge?.source) && nodeIds.has(edge?.target)
        ) ?? [];

      if (selectedNodes.length === 0) {
        setIsModalOpen(false);
      }

      cyRef.current = Cytoscape({
        headless: false,
        container: containerRef.current,
        elements: [
          ...selectedNodes.map((node) => instanceNodeRemote2Local(node)),
          ...validEdges.map((edge) => instanceEdgeRemote2Local(edge)),
        ],
        style: [
          {
            selector: "node",
            style: {
              "background-color": "data(color)",
              label: "data(label)",
              "text-wrap": "wrap",
              "text-valign": "bottom",
              "text-outline-width": 2,
              "text-outline-color": "#FFF",
              "font-size": "1em",
              "background-blacken": 0,
              "text-max-width": "100px",
            },
          },
          {
            selector: "edge",
            style: {
              width: 1,
              "line-color": "#ccc",
              label: "data(label)",
              "curve-style": "bezier",
              "target-arrow-shape": "triangle",
              "target-arrow-color": "#ccc",
              "font-size": "0.7em",
              "edge-text-rotation": "autorotate",
              "text-opacity": 1.0,
              opacity: 0.9,
            },
          },
        ],
        layout: defaultLayout,
      });

      let allNodes = [...selectedNodes, ...(fetchedNodesData.nodes || [])];

      // Use a Map to ensure uniqueness based on node id
      const uniqueNodesMap = new Map();
      allNodes.forEach((node) => {
        uniqueNodesMap.set(node.id, node);
      });

      // Get the unique nodes as an array
      allNodes = Array.from(uniqueNodesMap.values());

      let newEdges = fetchedNodesData.edges || [];

      if (topicsSet.size > 0 || isCopyLink) {
        allNodes = [...allNodes, ...nodes];
        newEdges = [...newEdges, ...edges];
        setShowEdgeCounts(true);
        // Store fetched data in Redux
        const newFetchedData = {
          nodes: nodes ?? [],
          edges: edges ?? [],
        };

        if (JSON.stringify(newFetchedData) !== JSON.stringify(fetchedData)) {
          setFetchedData(newFetchedData);
          dispatch(setFetchedNodesData(newFetchedData));
        }
      }

      allNodes = [...new Map(allNodes.map((node) => [node.id, node])).values()];
      newEdges = [...new Map(newEdges.map((edge) => [edge.id, edge])).values()];

      dispatch(
        setInitialNodesCount(
          selectedNodes.length - pinnedNodes.size > 0
            ? selectedNodes.length - pinnedNodes.size
            : 0
        )
      );

      const allNodeIds = new Set(allNodes?.map((node) => node?.id));

      let allValidEdges =
        newEdges?.filter(
          (edge) => allNodeIds.has(edge.source) && allNodeIds.has(edge.target)
        ) ?? [];

      // Remove duplicates from edges based on `source` and `target`
      const uniqueEdgesMap = new Map();
      allValidEdges.forEach((edge) => {
        const edgeKey = `${edge.source}-${edge.target}`;
        uniqueEdgesMap.set(edgeKey, edge);
      });

      // Get the unique edges as an array
      allValidEdges = Array.from(uniqueEdgesMap.values());

      // Call updateNodeLabels to dynamically update labels based on hidden edge count
      updatePaneNodeLabels(allNodes, allValidEdges);
      updatePinnedNodeStyles();

      return () => cyRef.current.destroy(); // Cleanup on component unmount
    } else {
      setIsModalOpen(false);
      setIsEdgeModalOpen(false);
    }
  }, [
    graphData,
    showEdgeCounts,
    pinnedNodes,
    dispatch,
    // getAllNodes,
    expandedNodes,
    // expandedNodesMap,
    fetchedData,
    isCopyLink,
    allExpandedNodes,
    uniqueNodes,
    expandedPinnedNodes,
  ]);

  useEffect(() => {
    if (cyRef.current) {
      const nodes = uniqueNodes;
      const { setupContextMenu } = NodeContextMenu({
        cy: cyRef.current,
        dispatch,
        isSuperAdmin,
        pinnedNodes,
        currentRole,
        nodes,
        fetchedData,
        hasFetchedChildNodes,
        setHasFetchedChildNodes,
        setShowEdgeCounts,
        updateNodeLabels,
        setAttachments,
        setIsInfoModal,
        setShowDraftModal,
        handleSelectClick,
        handleDeselectClick,
        getAllNodes,
        setLinkUrl,
        setLinkNodeName,
        toggleLinkModal,
        generateNodeLink,
        loadingNodes,
        setMessage,
        expandedNodes,
        allExpandedNodes,
        expandedNodesMap,
        fetchedNodesData,
        draftedNodes,
      });

      setupContextMenu();
    }
  }, [
    // cyRef, // Only re-run if cyRef changes
    getAllNodes,
    dispatch,
    pinnedNodes,
    currentRole,
    expandedNodes, // Only include state that directly influences the context menu // As needed
    expandedNodesMap,
    NodeContextMenu,
    fetchedData,
    uniqueNodes,
    isCopyLink,
    draftedNodes,
  ]);

  useEffect(() => {
    if (cyRef.current) {
      cyRef.current.on("cxttapstart", (event) => {
        const originalEvent = event.originalEvent;
        if (originalEvent) {
          originalEvent.preventDefault();
          originalEvent.stopPropagation();
        }
      });

      cyRef.current.on("tap", "edge", (event) => {
        const edgeId = event.target.id();

        // Find the clicked edge
        const clickedEdge = uniqueEdges.find(
          (edge) => edge.id === parseInt(edgeId)
        );

        if (clickedEdge) {
          // Find the source and target nodes
          const sourceNode = uniqueNodes.find(
            (node) => node.id === clickedEdge.source
          );

          const targetNode = uniqueNodes.find(
            (node) => node.id === clickedEdge.target
          );

          if (sourceNode && targetNode) {
            const updatedEdge = { ...clickedEdge, sourceNode, targetNode };
            setSelectedEdge(updatedEdge);
            setIsEdgeModalOpen(true);
            closeNodeModal();
          } else {
            console.warn(`Edge with ID ${edgeId} not found.`);
          }
        } else {
          console.warn(`Edge with ID ${edgeId} not found.`);
        }
      });

      cyRef.current.on("tap", "node", (event) => {
        const nodeId = event.target.id();

        const clickedNode = uniqueNodes.find(
          (node) => node.id === parseInt(nodeId)
        );

        if (clickedNode) {
          const nodeRelations = uniqueEdges
            .map((edge) => {
              let relationType = "";
              let relatedNodeName = "";
              let relationId = edge.id;

              if (edge.source === parseInt(nodeId)) {
                relationType = "to";

                const relatedNode = uniqueNodes.find(
                  (node) => node.id === edge.target
                );

                relatedNodeName = relatedNode
                  ? relatedNode.props.name
                  : "Unknown";
              } else if (edge.target === parseInt(nodeId)) {
                relationType = "from";

                const relatedNode = uniqueNodes.find(
                  (node) => node.id === edge.source
                );

                relatedNodeName = relatedNode
                  ? relatedNode.props.name
                  : "Unknown";
              }

              return {
                relationId,
                className: edge.class,
                relationType,
                relatedNodeName,
              };
            })
            .filter((relation) => relation.relationType !== "");

          const isExtensible = Object.isExtensible(clickedNode);
          const updatedNode = isExtensible
            ? { ...clickedNode }
            : { ...clickedNode };

          updatedNode.relations = nodeRelations;

          if (updatedNode.class === "Playbook") {
            const relatedPlaybookRules = uniqueEdges
              .filter(
                (edge) =>
                  edge.class === "HasPlaybookRule" &&
                  (edge.source === parseInt(nodeId) ||
                    edge.target === parseInt(nodeId))
              )
              .map((edge) => {
                const relatedNodeId =
                  edge.source === parseInt(nodeId) ? edge.target : edge.source;
                return uniqueNodes.find(
                  (node) =>
                    node.id === relatedNodeId && node.class === "PlaybookRule"
                );
              })
              .filter((node) => node !== undefined);

            updatedNode.relatedPlaybookRules = relatedPlaybookRules.map(
              (rule) => ({ ...rule })
            );

            updatedNode.relatedPlaybookRules.forEach((rule) => {
              rule.furtherPlaybookRules = fetchFurtherPlaybookRules(rule.id);
            });
          }

          setModalData(updatedNode);
        } else {
          console.warn(`Node with ID ${nodeId} not found.`);
        }

        setIsModalOpen(true);
        updatePinnedNodeStyles();
        closeEdgeModal();
      });

      cyRef.current.on("mouseover", "node", (e) => {
        const node = e.target;
        showHoverMsg(node);
      });

      cyRef.current.on("mouseout", "node", (e) => {
        showHoverMsg(null);
      });

      cyRef.current.on("tap", (event) => {
        cyRef.current.nodes().forEach((node) => {
          if (!pinnedNodes.has(node.id())) {
            // Reset styling for non-pinned nodes
            node.style({
              opacity: 1,
              "border-width": 0,
              "border-color": "transparent",
            });
          }
        });

        cyRef.current.edges().forEach((edge) => {
          edge.style({
            opacity: 1,
            "line-color": "#ccc",
          });
        });

        // Reapply styling for pinned nodes to ensure consistency
        updatePinnedNodeStyles();
      });
    }
  }, [
    cyRef,
    edges,
    fetchFurtherPlaybookRules,
    fetchedData,
    nodes,
    pinnedNodes,
    expandedNodes,
    expandedNodesMap,
    newEdge,
    newNode,
    fetchedNodesData,
    uniqueNodes,
    uniqueEdges,
  ]);

  useEffect(() => {
    if (cyRef.current && graphData) {
      cyRef.current.ready(() => {
        updatePinnedNodeStyles();
        dispatch(
          setInitialNodesCount(
            cyRef.current.nodes().length - pinnedNodes.size > 0
              ? cyRef.current.nodes().length - pinnedNodes.size
              : 0
          )
        );
      });
    }
  }, [cyRef, pinnedNodes, expandedNodes, showEdgeCounts, graphData]);

  const validateAndProcessEdges = (
    edges,
    availableNodes,
    fetchedNodes = []
  ) => {
    const allNodes = new Set([
      ...availableNodes.map((node) => String(node.id)),
      ...fetchedNodes.map((node) => String(node.id)),
    ]);

    return edges.filter((edge) => {
      const sourceExists = allNodes.has(String(edge.source));
      const targetExists = allNodes.has(String(edge.target));
      return sourceExists && targetExists;
    });
  };

  // In NodeSchemaViewPane.jsx
  // Update or add this useEffect for reset handling

  // useEffect(() => {
  //   if (cyRef.current) {
  //     if (
  //       (selectedLeafClasses?.length > 0 && expandedNodes.size === 0) ||
  //       isCopyLink
  //     ) {
  //       cyRef.current.nodes().forEach((node) => {
  //         fetchTopicNodesCount(node.id());
  //       });
  //       updatePinnedNodeStyles();
  //     } else {
  //       setIsModalOpen(false);
  //     }
  //   }
  // }, [selectedLeafClasses, isCopyLink]);
  // In NodeSchemaViewPane.jsx
  useEffect(() => {
    if (cyRef.current && resetRequested) {
      // Create a set with only pinned node IDs
      const preservedNodeIds = new Set([...pinnedNodes].map(String));

      cyRef.current.batch(() => {
        // Store positions of pinned nodes before layout
        const pinnedPositions = new Map();
        cyRef.current.nodes().forEach((node) => {
          const nodeId = String(node.id());
          if (pinnedNodes.has(nodeId)) {
            pinnedPositions.set(nodeId, node.position());
          }
        });

        // Remove non-preserved nodes and their edges
        cyRef.current.nodes().forEach((node) => {
          const nodeId = String(node.id());
          if (!preservedNodeIds.has(nodeId)) {
            node.connectedEdges().remove();
            node.remove();
          }
        });

        // Update styles and positions for remaining nodes
        cyRef.current.nodes().forEach((node) => {
          const nodeId = String(node.id());

          if (pinnedNodes.has(nodeId)) {
            // Restore pinned node styling
            node.style({
              "border-width": 5,
              "border-color": "red",
              "border-style": "double",
              "text-outline-width": 2,
            });
            node.locked(true);

            // Restore original position if available
            if (pinnedPositions.has(nodeId)) {
              node.position(pinnedPositions.get(nodeId));
            }
            // } else if (
            //   expandedNodes.has(nodeId) ||
            //   allExpandedNodes.has(nodeId)
            // ) {
            //   // Style for expanded nodes

            //   node.locked(false);
            // } else {
            //   // Unlock any non-pinned nodes (expanded but not pinned)
            //   node.locked(false);
          }
        });

        // Run layout only on non-pinned nodes
        const layout = cyRef.current.layout({
          ...defaultLayout,
          fit: true,
          // Exclude pinned nodes from layout
          eles: cyRef.current
            .elements()
            .filter(
              (ele) => !ele.isNode() || !pinnedNodes.has(String(ele.id()))
            ),
        });

        dispatch(
          setInitialNodesCount(
            cyRef.current?.nodes()?.length - pinnedNodes.size
          )
        );

        layout.run();
      });

      // Complete reset after layout
      completeReset();
    }
  }, [
    resetRequested,
    pinnedNodes,
    expandedNodes,
    allExpandedNodes,
    showEdgeCounts,
  ]);

  // useEffect(() => {
  //   if (cyRef.current && resetRequested) {
  //     const preservedNodeIds = new Set([
  //       ...[...pinnedNodes].map(String),
  //       ...[...expandedNodes].map(String),
  //       ...[...allExpandedNodes].map(String),
  //     ]);

  //     cyRef.current.batch(() => {
  //       // Remove non-preserved nodes
  //       cyRef.current.nodes().forEach((node) => {
  //         const nodeId = String(node.id());
  //         if (!preservedNodeIds.has(nodeId)) {
  //           // Remove connected edges first
  //           node.connectedEdges().remove();
  //           // Then remove the node
  //           node.remove();
  //         }
  //       });

  //       // Keep existing style logic
  //       // updatePinnedNodeStyles();

  //       // Run layout
  //       cyRef.current.layout(defaultLayout).run();
  //     });

  //     // updateNodeLabels();
  //     completeReset();
  //   }
  // }, [resetRequested]);

  const showHoverMsg = (data) => {
    setHoveredNode(data);
  };

  const handleNodeTap = (nodeId) => {
    const clickedNode = uniqueNodes.find(
      (node) => node.id === parseInt(nodeId)
    );

    if (clickedNode) {
      const nodeRelations = uniqueEdges
        .map((edge) => {
          let relationType = "";
          let relatedNodeName = "";
          let relationId = edge.id;

          if (edge.source === parseInt(nodeId)) {
            relationType = "to";
            const relatedNode = uniqueNodes.find(
              (node) => node.id === edge.target
            );
            relatedNodeName = relatedNode ? relatedNode.props.name : "Unknown";
          } else if (edge.target === parseInt(nodeId)) {
            relationType = "from";
            const relatedNode = uniqueNodes.find(
              (node) => node.id === edge.source
            );
            relatedNodeName = relatedNode ? relatedNode.props.name : "Unknown";
          }

          return {
            relationId,
            className: edge.class,
            relationType,
            relatedNodeName,
          };
        })
        .filter((relation) => relation.relationType !== "");

      const isExtensible = Object.isExtensible(clickedNode);
      const updatedNode = isExtensible
        ? { ...clickedNode }
        : { ...clickedNode };

      updatedNode.relations = nodeRelations;

      if (updatedNode.class === "Playbook") {
        const relatedPlaybookRules = uniqueEdges
          .filter(
            (edge) =>
              edge.class === "HasPlaybookRule" &&
              (edge.source === parseInt(nodeId) ||
                edge.target === parseInt(nodeId))
          )
          .map((edge) => {
            const relatedNodeId =
              edge.source === parseInt(nodeId) ? edge.target : edge.source;
            return uniqueNodes.find(
              (node) =>
                node.id === relatedNodeId && node.class === "PlaybookRule"
            );
          })
          .filter((node) => node !== undefined);

        updatedNode.relatedPlaybookRules = relatedPlaybookRules.map((rule) => ({
          ...rule,
        }));

        updatedNode.relatedPlaybookRules.forEach((rule) => {
          rule.furtherPlaybookRules = fetchFurtherPlaybookRules(rule.id);
        });
      }

      setModalData(updatedNode);
    } else {
      console.warn(`Node with ID ${nodeId} not found.`);
    }

    setIsModalOpen(true);
    closeEdgeModal();
  };

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function (...args) {
      if (!lastRan) {
        func.apply(this, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  };

  const handleZoomIn = throttle(() => {
    if (cyRef.current) {
      const newZoom = cyRef.current.zoom() * 1.2;

      // Calculate the center of the viewport
      const center = {
        x: cyRef.current.container().clientWidth / 2,
        y: cyRef.current.container().clientHeight / 2,
      };

      // Zoom to the new level, with the center as the focal point
      cyRef.current.zoom({
        level: newZoom,
        renderedPosition: center,
      });
    }
  }, 100);

  const handleZoomOut = throttle(() => {
    if (cyRef.current) {
      const newZoom = cyRef.current.zoom() / 1.2;

      // Calculate the center of the viewport
      const center = {
        x: cyRef.current.container().clientWidth / 2,
        y: cyRef.current.container().clientHeight / 2,
      };

      // Zoom to the new level, with the center as the focal point
      cyRef.current.zoom({
        level: newZoom,
        renderedPosition: center,
      });
    }
  }, 100);

  const handleResetZoom = () => {
    if (cyRef.current) {
      cyRef.current.fit();
    }
  };

  if (loadingNodes) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <span style={{ display: "flex", alignItems: "center" }}>
          <Spinner animation="border" />
        </span>
      </div>
    );
  }

  const startContinuousZoomIn = () => {
    if (!zoomInInterval.current) {
      zoomInInterval.current = setInterval(handleZoomIn, 100); // Adjust interval speed if needed
    }
  };

  const startContinuousZoomOut = () => {
    if (!zoomOutInterval.current) {
      zoomOutInterval.current = setInterval(handleZoomOut, 100);
    }
  };

  const stopContinuousZoom = () => {
    if (zoomInInterval.current) {
      clearInterval(zoomInInterval.current);
      zoomInInterval.current = null;
    }
    if (zoomOutInterval.current) {
      clearInterval(zoomOutInterval.current);
      zoomOutInterval.current = null;
    }
  };

  return (
    <>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: window.innerHeight - 120,
        }}
      >
        <div
          ref={containerRef}
          style={{ width: "100%", height: window.innerHeight - 130 }}
        />
        {graphData &&
          graphData.nodes &&
          graphData.nodes.length > 0 &&
          (!isPlaybookLink ? (
            <div className="zoom-controls">
              <div
                className="zoom-box"
                onClick={() => {
                  handleResetZoom();
                }}
              >
                <IoResize className="zoom-icon-reset" />
              </div>
              <div
                className="zoom-box"
                onMouseDown={startContinuousZoomIn}
                onMouseUp={stopContinuousZoom}
                onMouseLeave={stopContinuousZoom}
                onClick={handleZoomIn}
              >
                <FiPlus className="zoom-icon" />
              </div>
              <div
                className="zoom-box"
                onMouseDown={startContinuousZoomOut}
                onMouseUp={stopContinuousZoom}
                onMouseLeave={stopContinuousZoom}
                onClick={handleZoomOut}
              >
                <FiMinus className="zoom-icon" />
              </div>
            </div>
          ) : (
            <SchemaInfo
              show={true}
              handleClose={closeNodeModal}
              data={modalData}
              fetchedData={fetchedData}
              fetchFurtherPlaybookRules={fetchFurtherPlaybookRules}
              isPlaybook={isPlaybook}
              cy={cyschema}
              nodeSchema={cyRef.current}
              isPlaybookLink={isPlaybookLink}
              cyRef={cyRef.current}
              newEdge={newEdge}
              setNewEdge={setNewEdge}
              newNode={newNode}
              setNewNode={setNewNode}
              fetchTopicNodesCount={fetchTopicNodesCount}
            />
          ))}
      </div>
      {isModalOpen && (
        <SchemaInfo
          show={isModalOpen}
          handleClose={closeNodeModal}
          data={modalData}
          fetchedData={fetchedData}
          fetchFurtherPlaybookRules={fetchFurtherPlaybookRules}
          isPlaybook={isPlaybook}
          cy={cyschema}
          nodeSchema={cyRef.current}
          isPlaybookLink={isPlaybookLink}
          cyRef={cyRef.current}
          newEdge={newEdge}
          setNewEdge={setNewEdge}
          newNode={newNode}
          setNewNode={setNewNode}
          fetchTopicNodesCount={fetchTopicNodesCount}
        />
      )}
      {isEdgeModalOpen && (
        <EdgeInfoTab
          data={selectedEdge}
          show={isEdgeModalOpen}
          handleClose={closeEdgeModal}
          handleDeleteEdge={handleDeleteEdge}
        />
      )}
      {showPane && (
        <SelectPane
          isOpen={showPane}
          onClose={() => setShowPane(false)} // Pass the close handler to SelectPane
          nodeOid={selectedNodeOid} // Pass the selected OID to SelectPane
          clickedNode={clickedNode}
          selectedNodes={selectedNodes}
          setSelectedNodes={setSelectedNodes}
        />
      )}
      <DraftDocumentModal
        show={showDraftModal}
        handleClose={handleDraftModalClose}
        handleDraft={handleDraftAction}
        handleClear={handleClearAttachments} // Pass clear attachments function to the modal
        attachments={attachments} // Pass the attachments array to the modal
        isInfoModal={isInfoModal}
        message={message}
      />
      <FileNameModal
        show={showFileModal}
        handleClose={handleFileModalClose}
        handleDownload={handleDownload}
        filename={filename}
        setFilename={setFilename}
      />
      <LinkCopyModal
        isOpen={isLinkModalOpen}
        toggle={toggleLinkModal}
        url={linkUrl}
        nodeName={linkNodeName}
      />
      {hoveredNode && <Hoverbar nodeData={hoveredNode} isInstance={true} />}
    </>
  );
};

export default NodeSchemaViewPane;
